import { getLocaleString } from '@assets/i18n/config';
import type { SvgIconComponent } from '@mui/icons-material';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockIcon from '@mui/icons-material/Lock';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import SearchIcon from '@mui/icons-material/Search';
import TranslateIcon from '@mui/icons-material/Translate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  type InputAdornmentProps,
  InputAdornment as MuiInputAdornment,
  TextField as MuiTextField,
  type SxProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '@ovComponents/0-tokens/ref/palette/palette';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import {
  formatPhoneNumberForInput,
  getLocaleNumberSeparators,
} from '@utils/common';
import { dollars2cents } from '@utils/dollars2cents';
import {
  type CSSProperties,
  type FocusEventHandler,
  forwardRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import { IconButton } from '../iconButton/iconButton';
import InfoDialog from '../infoDialog/infoDialog';
import { Tooltip } from '../tooltip/tooltip';
import { useTextFieldTokens } from './textField.tokens';

export const InputAdornment = (props: InputAdornmentProps) => (
  <MuiInputAdornment {...props} />
);

type FormatterProps = {
  onChange: (event: {
    target: { valueCents: undefined | number; name: string; value: string };
  }) => void;
  name: string;
  decimalplaces: number;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};

interface CurrencyFormatterProps extends FormatterProps {
  value?: string | number | null;
}

export type TextFieldProps = {
  testId?: string;
  type?:
    | 'default'
    | 'multiLanguage'
    | 'number'
    | 'dollar'
    | 'phone'
    | '3-3-3'
    | '3-2-4'
    | '2-7'
    | 'password';
  label?: string;
  placeholder?: string;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  locked?: boolean;
  lockMessage?: string;
  sx?: SxProps;
  leadingIcon?: 'dollar' | 'percent' | 'search';
  trailingIcon?: 'calendar' | 'percent' | 'dollar';
  size?: 'small' | 'medium';
  textSize?: string | number;
  textAlign?: 'left' | 'center' | 'right';
  children?: React.ReactNode[] | React.ReactNode;
  select?: boolean;
  value?: any;
  defaultValue?: any;
  onChange?: any;
  onFocus?: any;
  onBlur?: any;
  onClick?: any;
  onPaste?: any;
  onTranslate?: any;
  renderValue?: any;
  InputProps?: any;
  inputProps?: any;
  multiline?: boolean;
  minRows?: number | string;
  maxRows?: number | string;
  rows?: number | string;
  infoTooltip?: string;
  multiple?: boolean;
  required?: boolean;
  onKeyDown?: any;
  menuTestId?: string;
  decimalPlaces?: number;
  visibilityToggle?: boolean;
  onVisibilityChange?: (reveal: boolean) => void;
  showArrows?: boolean;
  bottomLabel?: string;
  autocomplete?: 'on' | 'off';
  SelectProps?: any;
};

const StyledTextField = styled(MuiTextField)(() => {
  const tokens = useThemeTokens(useTextFieldTokens());
  const { comp } = tokens;

  return {
    '& .MuiOutlinedInput-root': {
      '& input': {
        paddingLeft: '12px',
        paddingRight: '12px',
        color: comp.textField.textColor,
        fontSize: comp.textField.fontSize,
        fontWeight: comp.textField.fontWeight,
        textAlign: 'inherit',
        '&::placeholder': {
          textAlign: 'inherit',
        },
        '&:disabled': {
          color: comp.textField.disabledColor,
        },
      },
      '&.hide-number-spinners input[type="number"]': {
        MozAppearance: 'textfield',
        '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
      '&.Mui-focused fieldset': {
        borderColor: comp.textField.focusBorderColor,
      },
      '& fieldset': {
        borderColor: comp.textField.borderColor,
      },
      '&.MuiInputBase-adornedStart .MuiInputAdornment-positionStart': {
        marginRight: '0px',
      },
      '& .MuiSelect-select': {
        paddingLeft: '0px',
        marginLeft: '12px',
        marginRight: '12px',
        color: comp.textField.textColor,
        fontSize: comp.textField.fontSize,
        fontWeight: comp.textField.fontWeight,
      },
      '& .MuiInputAdornment-positionEnd': {
        marginLeft: '0px',
      },
      '&.Mui-error': {
        '& input': {
          color: comp.textField.errorColor,
        },
        '& fieldset': {
          borderColor: comp.textField.errorColor,
        },
      },
    },
  };
});

const CurrencyFormatter = forwardRef<
  NumberFormat<string>,
  CurrencyFormatterProps
>((props, ref) => {
  const {
    onChange,
    decimalplaces = 2,
    onFocus: forwardedOnFocus,
    onBlur: forwardedOnBlur,
    value: rawValue,
    ...other
  } = props;

  // Pad displayed value with `fixedDecimalScale={false}` — focused
  // NumberFormat skips prop updates whose float matches its state, so typed
  // `35.4` stays on screen while we feed `"35.40"`. `setIsFocused`'s blur
  // re-render is what lets it finally repaint the padded form; don't gate
  // display logic on `isFocused` (NumberFormat's `setTimeout(0)` lags state).
  const [, setIsFocused] = useState(false);
  const { thousandSeparator, decimalSeparator } = getLocaleNumberSeparators();

  const displayValue = ((): string | number | null | undefined => {
    if (rawValue === '' || rawValue === undefined || rawValue === null) {
      return rawValue;
    }
    // Strip the current locale's grouping separators and rewrite its decimal
    // separator so `Number()` can interpret strings that callers have already
    // run through `Intl.NumberFormat` (e.g. `formatDecimalNumber` =>
    // `"12,345.00"`). Without this the toFixed pad below silently blanks the
    // input on initial render (e.g. ledger Update Transaction dialog).
    let normalizedValue: string | number = rawValue;
    if (typeof rawValue === 'string') {
      let stripped = rawValue;
      if (thousandSeparator) {
        stripped = stripped.split(thousandSeparator).join('');
      }
      if (decimalSeparator && decimalSeparator !== '.') {
        stripped = stripped.split(decimalSeparator).join('.');
      }
      normalizedValue = stripped;
    }
    const numericValue = Number(normalizedValue);
    if (!Number.isFinite(numericValue)) return '';
    return numericValue.toFixed(decimalplaces);
  })();

  return (
    <NumberFormat
      {...other}
      value={displayValue}
      getInputRef={ref}
      onValueChange={(values, sourceInfo) => {
        // !todo: onValueChange is triggered on any event or prop change - don't need that if it's not user generated,
        // it forces react to re-trigger state change and rebuild the portfolio table multiple times
        /** @see https://s-yadav.github.io/react-number-format/docs/props#onvaluechange-values-sourceinfo-- */
        if (sourceInfo.source !== 'event') return;
        const { value } = values;

        if (value !== '' && Number.isNaN(Number(value))) {
          return;
        }

        onChange({
          target: {
            name: props.name,
            value,
            valueCents:
              value === '' ? undefined : dollars2cents(value, decimalplaces),
          },
        });
      }}
      onFocus={(e) => {
        setIsFocused(true);
        forwardedOnFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        forwardedOnBlur?.(e);
      }}
      decimalScale={decimalplaces}
      fixedDecimalScale={false}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      isNumericString
      defaultValue={0}
      allowNegative={true}
      allowEmptyFormatting={false}
      allowLeadingZeros={false}
    />
  );
});

export const DigitFormatter333 = forwardRef<
  NumberFormat<string>,
  FormatterProps
>((props, ref) => {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
            valueCents: undefined,
          },
        });
      }}
      format={'### ### ###'}
      isNumericString
    />
  );
});

export const DigitFormatter324 = forwardRef<
  NumberFormat<string>,
  FormatterProps
>((props, ref) => {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
            valueCents: undefined,
          },
        });
      }}
      format={'###-##-####'}
      isNumericString
    />
  );
});

export const DigitFormatter27 = forwardRef<
  NumberFormat<string>,
  FormatterProps
>((props, ref) => {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
            valueCents: undefined,
          },
        });
      }}
      format={'##-#######'}
      isNumericString
    />
  );
});

export const PhoneFormatter = forwardRef<NumberFormat<string>, FormatterProps>(
  (props, ref) => {
    const { onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value ? `+${values.value}` : '',
              valueCents: undefined,
            },
          });
        }}
        format={formatPhoneNumberForInput}
        isNumericString
      />
    );
  },
);

export const TextField = ({
  label,
  type = 'default',
  helpText,
  error = false,
  errorText,
  success = false,
  successText,
  fullWidth,
  disabled = false,
  locked = false,
  lockMessage,
  sx,
  leadingIcon,
  trailingIcon,
  size = 'medium',
  onChange,
  onFocus,
  onBlur,
  onClick,
  onTranslate,
  renderValue,
  placeholder,
  children,
  select,
  value,
  infoTooltip,
  multiple,
  required,
  onKeyDown,
  testId = 'text-field',
  decimalPlaces = 2,
  visibilityToggle,
  onVisibilityChange,
  showArrows = true,
  textSize = '16px',
  textAlign = 'left',
  bottomLabel,
  autocomplete,
  menuTestId,
  ...restProps
}: TextFieldProps) => {
  const { sys, comp } = useThemeTokens(useTextFieldTokens());
  const { comp: helpTextComp } = useThemeTokens(useHelpTextTokens());
  const { t } = useTranslation('components');
  const [visibility, setVisibility] = useState<boolean>(false);
  const variantStyle: CSSProperties = {};

  const shouldDisplayInDialog = helpTextComp.helpText.displayInDialog;
  const helpTextToShow =
    infoTooltip && !shouldDisplayInDialog ? infoTooltip : helpText;

  let effectiveLeadingIcon = leadingIcon;
  let effectiveTrailingIcon = trailingIcon;

  if (type === 'dollar') {
    const symbolAfter = !getLocaleString().startsWith('en');
    const hasLeadingDollar = leadingIcon === 'dollar';
    const hasTrailingDollar = trailingIcon === 'dollar';

    if (!hasLeadingDollar && !hasTrailingDollar) {
      effectiveLeadingIcon = symbolAfter ? undefined : 'dollar';
      effectiveTrailingIcon = symbolAfter ? 'dollar' : undefined;
    } else if (hasLeadingDollar && !hasTrailingDollar && symbolAfter) {
      // Backward-compatible migration path: legacy leading dollar icon should trail in non-English locales.
      effectiveLeadingIcon = undefined;
      effectiveTrailingIcon = 'dollar';
    }
  }

  let leadingIconComponent: SvgIconComponent | null = null;
  let trailingIconComponent: SvgIconComponent | null = null;

  switch (effectiveLeadingIcon) {
    case 'dollar':
      leadingIconComponent = AttachMoneyRoundedIcon;
      break;
    case 'percent':
      leadingIconComponent = PercentRoundedIcon;
      break;
    case 'search':
      leadingIconComponent = SearchIcon;
      break;
  }
  switch (effectiveTrailingIcon) {
    case 'calendar':
      trailingIconComponent = CalendarMonthIcon;
      break;
    case 'percent':
      trailingIconComponent = PercentRoundedIcon;
      break;
    case 'dollar':
      trailingIconComponent = AttachMoneyRoundedIcon;
      break;
  }

  const style = {
    ...variantStyle,
    height: restProps.multiline ? 'auto' : size === 'medium' ? '40px' : '32px',
    fontSize: textSize,
    fontFamily: sys.font.base,
    fontWeight: comp.textField.fontWeight,
    borderRadius: '4px',
    width: fullWidth ? '100%' : 'auto',
    background: colors.white,
  };

  const footerText = error
    ? (errorText ?? t('fieldErrors.default', { fieldName: label }))
    : success
      ? successText
      : helpTextToShow;

  const getFormatter = (): any => {
    switch (type) {
      case 'dollar': {
        return CurrencyFormatter;
      }
      case '3-3-3': {
        return DigitFormatter333;
      }
      case '3-2-4': {
        return DigitFormatter324;
      }
      case '2-7': {
        return DigitFormatter27;
      }
      case 'phone': {
        return PhoneFormatter;
      }
      default: {
        return undefined;
      }
    }
  };

  const htmlInputType =
    type === 'number'
      ? 'number'
      : type === 'phone'
        ? 'tel'
        : type === 'password'
          ? 'password'
          : 'text';

  const endAdornment: JSX.Element[] = [];

  if (locked) {
    endAdornment.push(
      <Tooltip title={lockMessage ?? ''} key='lock-icon'>
        <Icon icon={LockIcon} size='small' />
      </Tooltip>,
    );
  }
  const iconButtonSize =
    style.height === 'auto'
      ? 'auto'
      : `${Number(style.height.slice(0, -2)) - 8}px`;

  if (type === 'multiLanguage') {
    endAdornment.push(
      <IconButton
        key='translate-icon'
        style={{
          height: iconButtonSize,
          width: iconButtonSize,
        }}
        onClick={locked || disabled ? null : onTranslate}
      >
        <Icon icon={TranslateIcon} size='small' />
      </IconButton>,
    );
  }

  if (visibilityToggle) {
    endAdornment.push(
      <IconButton
        key='visibility-icon'
        style={{
          height: iconButtonSize,
          width: iconButtonSize,
        }}
        onClick={(e) => {
          setVisibility(!visibility);
          onVisibilityChange?.(!visibility);
          e.stopPropagation();
        }}
      >
        <Icon
          icon={visibility ? VisibilityIcon : VisibilityOffIcon}
          size='small'
        />
      </IconButton>,
    );
  }

  if (trailingIconComponent) {
    endAdornment.push(
      <Icon icon={trailingIconComponent} size='small' key='trailing-icon' />,
    );
  }

  return (
    <Box sx={sx}>
      <Box display='flex' flexDirection='row' alignItems={'center'}>
        <Typography
          variant='labelSmall'
          sx={{
            mb: label ? '4px' : '0px',
            display: 'flex',
            alignItems: 'center',
            color: error
              ? comp.textField.errorColor
              : comp.textField.labelColor,
          }}
          data-testid={`${testId}-label`}
        >
          {label}
          {required && (
            <span style={{ color: comp.textField.requiredIndicatorColor }}>
              *
            </span>
          )}
          {infoTooltip && shouldDisplayInDialog && (
            <InfoDialog sx={{ display: 'block' }} information={infoTooltip} />
          )}
        </Typography>
      </Box>
      <StyledTextField
        {...restProps}
        data-testid={testId ?? 'text-field'}
        type={htmlInputType}
        onClick={onClick}
        onChange={onChange}
        error={!!error}
        style={style}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled || locked}
        placeholder={placeholder}
        select={select}
        value={value}
        required={required}
        autoComplete={autocomplete || 'on'}
        InputProps={{
          style,
          className:
            type === 'number' && !showArrows ? 'hide-number-spinners' : '',
          inputProps: {
            ...(restProps.inputProps ?? {}),
            'data-testid': `${testId ?? 'text-field'}-input`,
            decimalplaces: decimalPlaces,
            step: 'any',
            onInvalid: (e: React.InvalidEvent<HTMLInputElement>) => {
              const message =
                htmlInputType === 'number'
                  ? t('fieldValidation.enterNumber')
                  : t('fieldValidation.required');
              e.target.setCustomValidity(message);
            },
            onInput: (e: React.FormEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).setCustomValidity('');
            },
          },
          inputComponent: getFormatter(),
          startAdornment: leadingIconComponent ? (
            <MuiInputAdornment position='start'>
              <Icon icon={leadingIconComponent} size='small' />
            </MuiInputAdornment>
          ) : null,
          endAdornment: endAdornment.length > 0 && (
            <MuiInputAdornment position='end'>
              {' '}
              {endAdornment}{' '}
            </MuiInputAdornment>
          ),
          ...restProps.InputProps,
        }}
        onKeyDown={onKeyDown}
        SelectProps={
          select
            ? {
                renderValue,
                ...(locked ? { IconComponent: 'span' } : {}),
                multiple: multiple ?? undefined,
                MenuProps: {
                  PaperProps: {
                    'data-testid': `${menuTestId}`,
                  },
                  ...restProps.SelectProps?.MenuProps,
                },
                ...restProps.SelectProps,
              }
            : undefined
        }
      >
        {children}
      </StyledTextField>
      {footerText && (
        <HelpText
          text={footerText}
          tone={error ? 'error' : success ? 'success' : 'neutral'}
          showIcon={
            !(!shouldDisplayInDialog && !error && !success && !!helpTextToShow)
          }
        />
      )}
      {bottomLabel && (
        <Typography
          variant='labelSmall'
          sx={{
            mt: '4px',
            color: error
              ? comp.textField.errorColor
              : comp.textField.labelColor,
          }}
          data-testid={`${testId}-bottom-label`}
        >
          {bottomLabel}
        </Typography>
      )}
    </Box>
  );
};
